/* eslint-disable max-len */
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form'; 
import Select from 'react-select'; 
import { COURSE_TYPE, COURSE_TYPE_STRING, LEVEL_COURSE_STRING, LEVEL_COURSE_TYPE, PUBLIC_DATE_FILTER_STRING, PUBLIC_DATE_FILTER_TYPE, SYSTEM_PATH, USER_TYPE } from '../../core/configs/constants';
import { useQuery, useStore } from '../../core/utils/hook';
import CourseCard from './components/CoursCard';
import Pagination from './components/Pagination';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toJS } from 'mobx';
import { TreeViewCustom } from '../../components';

function CoursesScreen() {

    const navigate = useNavigate();
    const location = useLocation();
    const expandedIdsState = location.state?.expandedIds?.length > 0 ? location.state.expandedIds : [];
    
    // state for search field
    const [searchField, setSearchField] = useState('courseName');
    const [topOffset, setTopOffset] = useState(0);
    const [topFilterOffset, setTopFilterOffset] = useState(0);
    const [topBreadcrumbOffset, setTopBreadcrumbOffset] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const header = document.getElementById('header-site');
        if (!header) return;

        const updateHeight = () => {
            setHeaderHeight(header.offsetHeight);
        };

        // Follow initial height header
        updateHeight();

        // Follow resize header
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(header);

        // Follow resize window
        window.addEventListener('resize', updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);
    

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1225);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const header = document.getElementById('header-site');
        const filterSection = document.getElementById('top-student-list');
        const breadcrumbSection = document.getElementById('bres-cus-courses-screen');
        if (!header || !filterSection || !breadcrumbSection) return;



        // get initial height
        setTopOffset(header.offsetHeight);
        setTopFilterOffset(filterSection.offsetHeight);
        // console.log(breadcrumbSection.offsetHeight);
        setTopBreadcrumbOffset(breadcrumbSection.offsetHeight);
        

        // observe header and filter section size change
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.target === header) {
                    setTopOffset(entry.contentRect.height);
                }
                if (entry.target === filterSection) {
                    setTopFilterOffset(entry.contentRect.height);
                }
                if (entry.target === breadcrumbSection) {
                    setTopBreadcrumbOffset(entry.contentRect.height);
                }
            }
        });

        resizeObserver.observe(header);
        resizeObserver.observe(filterSection);
        resizeObserver.observe(breadcrumbSection);

        return () => resizeObserver.disconnect();
    }, []);

    // store
    const {
        courseStore: { getAllCourses, allCourses, pagingAllCourses, setAttrObservable, cleanCourseData, searchCourseInfo, setSearchCourseInfo },
        themeStore: { themeTree, getAllThemesTreeView, canExpandIds },
        authStore: { token, userInfo }
    } = useStore();
    const query = useQuery();
    const themeId = query.get('themeId') || undefined;

    // state
    const { register, control, handleSubmit, getValues, reset, setValue } = useForm({
        defaultValues: {
            courseType: null,
            level: [],
            courseStatus: [] 
        }
    });

    const [expandedIds, setExpandedIds] = useState([]);
    const [highlightExpand, setHighlightExpand] = useState({
        key: 'themeId',
        value: themeId
    })

    // --- PREPARE OPTIONS FOR REACT-SELECT ---

    // Level Options
    const levelOptions = Object.keys(LEVEL_COURSE_TYPE).map(key => ({
        value: LEVEL_COURSE_TYPE[key],
        label: LEVEL_COURSE_STRING[LEVEL_COURSE_TYPE[key]]
    }));

    // Course Type Options
    const courseTypeOptions = [
        { value: '', label: '全て' },
        ...Object.keys(COURSE_TYPE).map(key => ({
            value: COURSE_TYPE[key],
            label: COURSE_TYPE_STRING[key]
        }))
    ];


    //  Course Status Options
    const courseStatusOptions = [
        { value: 0, label: '未受講' },
        { value: 1, label: '受講中' }, 
        { value: 2, label: '受講完了' }  
    ];

    useEffect(() => {
        cleanCourseData();
        getAllThemesTreeView({ treeView: true });
        history.replaceState({}, '');
    }, [])

    useEffect(() => {
        if (searchCourseInfo) {
            setValue('courseName', searchCourseInfo.name || '');
            const values = getValues();
            const payload = {
                ...values,
                ...(themeId ? { themeId } : {}),
                isCourseList: true
            };
            getAllCourses(payload);
            setSearchCourseInfo(null);
        } else {
            handleSearch();
        }
    }, [themeId]);

    useEffect(() => {
        if (themeTree?.length > 0) {
            if (expandedIdsState?.length > 0) {
                if (expandedIdsState.every(id => canExpandIds.includes(id))) {
                    setExpandedIds(expandedIdsState);
                }
            }
        }
    }, [themeTree])

    useEffect(() => {
        setHighlightExpand({
            key: 'themeId',
            value: themeId
        })
    }, [themeId])

    const buildCourseSearchPayload = () => {
        const values = getValues();

        if (values.courseType === 'DEFAULT') {
            values.courseType = '';
        }

        if (values.courseName) values.courseName = values.courseName.trim();
        if (values.keySearch) values.keySearch = values.keySearch.trim();

        if (searchField === 'courseName') {
            values.keySearch = '';
        } else {
            values.courseName = '';
        }

        const payload = { ...values };

        if (payload.coursePublicDate === false) delete payload.coursePublicDate;
        if (payload.courseStatus === false) delete payload.courseStatus;

        if (values?.level && values.level.length > 0) {
            payload.level = values.level.map(item => Number(item))
        }

        if (values?.courseStatus && values.courseStatus.length > 0) {
            payload.courseStatus = values.courseStatus.map(item => Number(item));
        } else {
            delete payload.courseStatus;
        }

        if (values?.coursePublicDate) {
            payload.coursePublicDate = Number(payload.coursePublicDate);
            if (values?.coursePublicDate == PUBLIC_DATE_FILTER_TYPE.DEFAULT) {
                payload.coursePublicDate = null;
            }
        }

        return payload;
    }

    const handleSearch = (condition = {}) => {
        setAttrObservable('pagingAllCourses', { page: 1 }, true, false);
        const payload = buildCourseSearchPayload();

        getAllCourses({
            ...payload,
            ...(themeId ? { themeId } : {}),
            ...condition,
            isCourseList: true
        });

        setHighlightExpand({
            key: 'themeId',
            value: themeId
        })
    };

    const handleChangePage = () => {
        const payload = buildCourseSearchPayload();

        window.$('html, body').animate({ scrollTop: 0 }, 250, 'easeInOutExpo');

        getAllCourses({
            ...payload,
            isCourseList: true,
            ...(themeId ? { themeId } : {})
        });
    };

    const onClickTreeName = (element) => {
        if (element?.metadata) {
            if (element.metadata.isTheme) {
                onHandLeExpand(element, true);
                if (themeId != element.metadata.themeId) {
                    navigate(`${SYSTEM_PATH.THEMES}?themeId=${element.metadata.themeId}`);
                } else {
                    handleSearch({ page: 1 });
                    setHighlightExpand({
                        key: 'themeId',
                        value: themeId
                    })
                }
            } else if (element.metadata.isCourse) {
                if (token) {
                    navigate(`/course/${element.metadata.courseId}`, { state: { expandedIds } });
                } else {
                    onResetSearch();
                    setHighlightExpand({
                        key: 'courseId',
                        value: element.metadata.courseId
                    })
                    getAllCourses({
                        courseId: element.metadata.courseId,
                        page: 1,
                        isCourseList: true
                    })
                }
            } else if (element.metadata.isLecture && element.metadata.isAttend) {
                navigate(`/lecture/${element.metadata.lectureId}`)
            }
        }
    }

    const onHandLeExpand = (e, alwaysAdd = false) => {
        if (alwaysAdd || !expandedIds.includes(e.id)) {
            setExpandedIds([...expandedIds, e.id]);
        } else {
            if (e.children?.length > 0) {
                const closeIds = [e.id, ...e.children];
                setExpandedIds([...expandedIds].filter(id => !closeIds.includes(id)));
            }
        }
    }

    const elementClass = (element) => {
        if (highlightExpand.key && highlightExpand.value) {
            if (element?.metadata?.[highlightExpand.key] == highlightExpand.value) {
                return 'tree-name-highlight';
            }
        } return '';
    }

    const onResetSearch = (payload = {}) => {
        reset({
            courseName: '',
            keySearch: '',
            courseType: null,
            level: [],
            courseStatus: [],
            coursePublicDate: PUBLIC_DATE_FILTER_TYPE.DEFAULT,
            ...payload
        })
    }

    const onSelectAllTheme = () => {
        if (themeId) {
            navigate(SYSTEM_PATH.THEMES);
        } else {
            handleSearch();
        }
    }

    const onDownloadCoursePdf = () => {
        window.open(`${window.location.origin}/files/elephamt講座一覧.pdf`, '_blank');
    }

    // Common styles for React Select
    const reactSelectStyles = {
        multiValue: (base) => ({ ...base, fontSize: '16px' }),
        menu: (base) => ({ ...base, zIndex: 9999 })
    };

  
    const handleChangeSearchType = (e) => {
        const newType = e.target.value; 
        const currentText = getValues(searchField); 

        
        setValue(searchField, ''); 
        
        
        setValue(newType, currentText);

    
        setSearchField(newType);
    }

    return (
        <div
            id="main"
            className="wrapper main-student-list main-Attended-couse main-view-course main-view-theme course-list-screen"
        >
            <div className="bres-cus content-view-course content-main-view-course" id='bres-cus-courses-screen' style={{ padding: '10px 0 16px 0px', position: 'sticky', zIndex: 50, top: `${headerHeight}px`, left: '0', width: '100%', backgroundColor: '#FFF'}}>
                <div className='container'>
                    <ul>
                        <li>
                            <Link to={SYSTEM_PATH.HOME}><img src='/images/icon-home.png' alt='' /></Link> 
                        </li>
                        <li className='dash'>\</li>
                        <li>講座一覧</li>
                    </ul>
                </div>
            </div>
            <div className="content-view-course content-main-view-course">
                <div className="container" style={{
                    position: 'relative'
                  
                }}>
                    
                    {/* --- FILTER SECTION --- */}
                   
                    <section
                        className='top-student-list'
                        id='top-student-list'
                        style={isMobile
                            ? { position: 'static', top: 'auto', backgroundColor: '#fff', zIndex: 'auto' }
                            : { position: 'sticky', top: `${topOffset + topBreadcrumbOffset + 25}px`, backgroundColor: '#fff', zIndex: 40 }}
                    >
                        <form className="h-adr" onSubmit={handleSubmit(handleSearch)}>
                            <div className="content-top-student theme-filter">
                                <h3 className="title-secondary mt-0 mg-b-20">講座検索</h3>
                                
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',   
                                        gap: '8px',
                                        width: '100%' 
                                    }}
                                >
                                    {/* 1. SEARCH COMBO BOX (Name/Free Word) */}
                                    <div style={{ width: 'fit-content', minWidth: '250px', flexGrow: 1 }}>
                                        <div 
                                            className="d-flex align-items-center w-100" 
                                            style={{ 
                                                
                                                border: '1px solid #B0B5BE', borderRadius: '4px', backgroundColor: '#fff',
                                                padding: '5px 10px', height: '48px'
                                            }}
                                        >
                                            <span className="d-flex align-items-center text-muted" style={{ marginRight: '10px' }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M21 21L16.65 16.65" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </span>
                                            <input 
                                                type='text' 
                                                id="custom-search-input"
                                                className="flex-grow-1"
                                                placeholder="キーワードを入力"
                                                {...register(searchField)} 
                                                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '14px', width: '100%' }}
                                            />
                                            <div style={{ width: '1px', height: '24px', backgroundColor: '#ddd', margin: '0 5px' }}></div>
                                            <select 
                                                value={searchField}
                                                className='search-type-select'
                                                onChange={handleChangeSearchType}
                                                style={{ 
                                                    border: 'none', outline: 'none', background: 'transparent', cursor: 'pointer',
                                                    fontSize: '14px', color: '#333', fontWeight: '500', textAlign: 'left', maxWidth: '220px'
                                                }}
                                            >
                                                <option value="keySearch">フリーワードで検索</option>
                                                <option value="courseName">講座名で検索</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* 2. COURSE TYPE SELECT */}
                                   
                                    <div style={{ minWidth: '200px', flexGrow: 1 }}>
                                        <Controller
                                            control={control}
                                            name="courseType"
                                            render={({ field: { onChange, value, ref } }) => (
                                                <Select
                                                    ref={ref}
                                                    className="react-select"
                                                    value={
                                                        courseTypeOptions.find(option => option.value === value) || null
                                                    }
                                                    onChange={(selectedOption) => {
                                                        onChange(selectedOption ? selectedOption.value : '');
                                                    }}
                                                    styles={{
                                                        ...reactSelectStyles,
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '48px',
                                                            height: '48px',
                                                      
                                                            borderColor: '#B0B5BE',
                                                            boxShadow: 'none'
                                                        })
                                                    }}
                                                    isMulti={false}
                                                    isSearchable={true}
                                                    closeMenuOnSelect={true}
                                                    placeholder="認定制度別"
                                                    noOptionsMessage={() => 'データはありません'}
                                                    options={courseTypeOptions}
                                                />
                                            )}
                                        />
                                    </div>


                                    {/* 3. COURSE STATUS (Multi Select) */}
                                    {
                                        token && ![USER_TYPE.GROUP_MANAGER].includes(userInfo?.roleId) &&
                                        <div style={{ minWidth: '200px', flexGrow: 1 }}>
                                            <Controller
                                                control={control}
                                                name="courseStatus"
                                                render={({ field: { onChange, value, ref } }) => (
                                                    <Select
                                                        ref={ref}
                                                        className='react-select'
                                                        styles={{
                                                            ...reactSelectStyles,
                                                            control: (base) => ({
                                                                ...base,
                                                                paddingLeft: '5px',
                                                                minHeight: '48px',
                                                                height: '48px',
                                                                borderColor: '#B0B5BE',
                                                                boxShadow: 'none'
                                                            })
                                                        }}
                                                        // Filter by value option in the form
                                                        value={courseStatusOptions.filter(option => 
                                                            Array.isArray(value) && value.map(Number).includes(option.value)
                                                        )}
                                                       
                                                        onChange={(selectedOptions) => {
                                                            onChange(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
                                                        }}
                                                        isMulti={true}
                                                        isSearchable={false}
                                                        closeMenuOnSelect={false}
                                                        placeholder='受講ステータス'
                                                        noOptionsMessage={() => 'データはありません'}
                                                        options={courseStatusOptions}
                                                    />
                                                )}
                                            />
                                        </div>
                                    }

                                    {/* 4. LEVEL (Multi Select) */}
                                    {
                                        allCourses?.length > 0 && allCourses.filter(course => course.level)?.length > 0 && (
                                            <div style={{  minWidth: '200px', flexGrow: 1 }}>
                                                <Controller
                                                    control={control}
                                                    name="level"
                                                    render={({ field: { onChange, value, ref } }) => (
                                                        <Select
                                                            ref={ref}
                                                            className='react-select'
                                                            value={levelOptions.filter(option => Array.isArray(value) && value.includes(option.value))}
                                                            onChange={(selectedOptions) => {
                                                                onChange(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
                                                            }}
                                                            styles={{
                                                                ...reactSelectStyles,
                                                                control: (base) => ({
                                                                    ...base,
                                                                    minHeight: '48px',
                                                                    height: '48px',
                                                                    borderColor: '#B0B5BE',
                                                                    boxShadow: 'none'
                                                                })
                                                            }}
                                                            isMulti={true}
                                                            isSearchable={true}
                                                            closeMenuOnSelect={false}
                                                            placeholder='レベル'
                                                            noOptionsMessage={() => 'データはありません'}
                                                            options={levelOptions}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        )
                                    }
                                    
                                    {/* 5. SEARCH BUTTON */}
                                    <div className="text-center" style={{ display: 'flex', justifyContent: 'start'}}>
                                        <input type="submit" value="検索" style={{ height: '48px', marginTop: 0 }} />
                                    </div>
                                </div>      
                            </div>
                        </form>
                    </section>
                    
                    {/* --- BOTTOM SECTION (Tree & List) --- */}
                    <div className='content-view-course-bottom'>
                        <div className='theme-tree-view' style={
                            isMobile
                                ? {} 
                                : {
                                    position: 'sticky',
                                    top: topOffset + topFilterOffset + topBreadcrumbOffset + 50,
                                    backgroundColor: '#fff',
                                    zIndex: 30,
                                    maxHeight: 'calc(100vh - ' + (topOffset + topFilterOffset + topBreadcrumbOffset + 75) + 'px)',
                                    overflowY: 'auto'
                                }
                        }>
                            <div className='tree-all-node fw-bold' onClick={onSelectAllTheme} style={{ fontSize: '15px' }}>すべてのテーマ</div>
                            <TreeViewCustom
                                data={{ name: 'すべてのテーマ', children: toJS(themeTree) }}
                                onClickTreeName={onClickTreeName}
                                onHandLeExpand={onHandLeExpand}
                                expandedIds={expandedIds}
                                elementClass={elementClass}
                            />
                        </div>

                        <div className="view-course-right">
                            <div className="title-search">
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                    
                                    {token && (
                                        
                                        <button className='w-auto btn-export-course-list' onClick={onDownloadCoursePdf}>講座一覧PDF</button>
                                            
                                        
                                    )}
                                    <div className='d-flex align-items-center flex-gap-10 flex-wrap'>
                                        <h3 className="title-2 mg-b-0 flex-none">{pagingAllCourses?.totalRecord}件</h3>
                                        <div className='separation' style={{ borderRight: '1px solid #AAAA', height: '20px' }}></div>
                                        <div className='min-width-200 public-date-filter' style={{
                                            backgroundColor: '#F5F6F8 !important'
                                        }}>
                                            <select {...register('coursePublicDate', {
                                                onChange: () => {
                                                    handleSearch();
                                                }
                                            })} className='min-width-200 pd-l-30' style={{ fontWeight: '700' }}>
                                                {Object.keys(PUBLIC_DATE_FILTER_TYPE).map((type, index) => (
                                                    <option key={index} value={PUBLIC_DATE_FILTER_TYPE[type]}>{PUBLIC_DATE_FILTER_STRING[PUBLIC_DATE_FILTER_TYPE[type]]}</option>
                                                ))}
                                            </select>
                                            <img className='public-date-filter-icon' src='/images/icon-filter.svg' alt='' />
                                            <img className='public-date-icon-mobile' src='/images/icon-arrow-down.svg' alt='' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="content-course-right">
                                {(allCourses || []).map((item) => (
                                    <CourseCard key={item.id} course={item} onFetchData={() => navigate(`${SYSTEM_PATH.COURSE}${item.id}`)} handleSearch={handleSearch} />
                                ))}
                                {!allCourses?.length && (
                                    <div className="no-data w-100"><p>データはありません</p></div>
                                )}
                            </div>
                            {allCourses?.length > 0 && <Pagination paging={pagingAllCourses} onFetch={handleChangePage} />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default observer(CoursesScreen);